import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { Badge, Button, Flex, List, Popover, Spin as AntdSpin, Tooltip, Typography } from 'antd';
import { Icon } from '@eduzz/ui-antd-theme';

import Truncate from 'components/Truncate';
import formatDistanceToNow from 'date-fns/formatDistanceToNow';
import ptBRLocale from 'date-fns/locale/pt-BR';
import { useModerateAffiliate } from 'v2/hooks/useModerateAffiliate';
import { usePendingAffiliatesSummaryCount } from 'v2/hooks/usePendingAffiliatesSummaryCount';
import { usePendingAffiliatesSummaryList } from 'v2/hooks/usePendingAffiliatesSummaryList';

import Topbar from '@eduzz/ui-layout/Topbar';

const POLLING_INTERVAL_MS = 300000;
const MAX_VISIBLE_AFFILIATIONS = 4;

const AffiliationsNotification = () => {
  const [popoverOpen, setPopoverOpen] = useState(false);

  const {
    summaryList,
    isLoading: isLoadingSummaryList,
    refetch: refetchSummaryList
  } = usePendingAffiliatesSummaryList();
  const notificationsShortList = useMemo(() => summaryList?.list?.slice(0, MAX_VISIBLE_AFFILIATIONS), [summaryList]);
  const {
    summaryCount,
    isLoading: isLoadingSummaryCount,
    refetch: refetchSummaryCount
  } = usePendingAffiliatesSummaryCount();
  const isAllLoading = isLoadingSummaryList && isLoadingSummaryCount;

  const { moderateAffiliate } = useModerateAffiliate();

  useEffect(() => {
    refetchSummaryCount();

    const intervalId = setInterval(() => {
      refetchSummaryCount();
    }, POLLING_INTERVAL_MS);

    return () => {
      clearInterval(intervalId);
    };
  }, [refetchSummaryCount]);

  const handleModerateAffiliation = useCallback(
    async (affiliateId: number, status: 'A' | 'R') => {
      const clearCache = (summaryList?.list || []).length - 1 === 0 && (summaryCount?.count || 0) - 1 > 0;

      await moderateAffiliate({ affiliateId, status });
      await refetchSummaryCount();
      await refetchSummaryList(clearCache);
    },
    [moderateAffiliate, refetchSummaryCount, refetchSummaryList, summaryList?.list, summaryCount?.count]
  );

  const handleClose = useCallback(() => {
    setPopoverOpen(false);
  }, []);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setPopoverOpen(open);

      if (open) {
        refetchSummaryList();
      }
    },
    [refetchSummaryList]
  );

  const popoverContent = (
    <List
      className='min-w-70'
      loading={(!!summaryList && isLoadingSummaryList) || isAllLoading}
      header={
        <div className='flex justify-center'>
          <Typography.Text strong>Solicitações de afiliação</Typography.Text>
        </div>
      }
      footer={
        <Link to='/producer/myaffiliates' onClick={handleClose}>
          <Button id='see-all-affiliations-button' className='uppercase' type='link' block>
            Ver todas
          </Button>
        </Link>
      }
      locale={{ emptyText: 'Nenhuma solicitação de afiliação' }}
      dataSource={!isAllLoading && !isLoadingSummaryList ? notificationsShortList : []}
      renderItem={affiliation => (
        <List.Item
          key={affiliation.id}
          actions={[
            <div className='flex' key='actions'>
              <Tooltip title='Aprovar' placement='top'>
                <Button
                  id={`affiliation-approve-${affiliation.id}-button`}
                  className='text-(--uizz-antd-color-success)!'
                  type='text'
                  shape='circle'
                  icon={<Icon.ThumbsUp  />}
                  onClick={() => handleModerateAffiliation(affiliation.id, 'A')}
                />
              </Tooltip>
              <Tooltip title='Reprovar' placement='top'>
                <Button
                  id={`affiliation-reprove-${affiliation.id}-button`}
                  type='text'
                  shape='circle'
                  icon={<Icon.ThumbsDown  />}
                  onClick={() => handleModerateAffiliation(affiliation.id, 'R')}
                />
              </Tooltip>
            </div>
          ]}
        >
          <Link to='/producer/myaffiliates' onClick={handleClose}>
            <div className='flex flex-col'>
              <Typography.Text strong>
                <Truncate count={25} final='...'>
                  {affiliation.affiliate_name}
                </Truncate>
              </Typography.Text>
              <Typography.Text type='secondary'>
                <Truncate count={25} final='...'>
                  {affiliation.product_title}
                </Truncate>
              </Typography.Text>
              <Typography.Text type='secondary'>
                {affiliation.request_date &&
                  formatDistanceToNow(new Date(affiliation.request_date), { addSuffix: true, locale: ptBRLocale })}
              </Typography.Text>
            </div>
          </Link>
        </List.Item>
      )}
    />
  );

  return (
    <Popover
      open={popoverOpen}
      onOpenChange={handleOpenChange}
      content={popoverContent}
      zIndex={999}
      trigger='click'
    >
      <Topbar.Action
        id='topbar-action-affiliates-requests'
        aria-label='mostrar solicitações'
        icon={isLoadingSummaryCount ? <Icon.Loader2 className='animate-spin' /> : <Icon.Users />}
        tooltip='Solicitações de afiliação'
        badgeCount={summaryCount?.count}
      />
    </Popover>
  );
};

export default AffiliationsNotification;
